import { AboutPage } from './about/AboutPage';
import { AuthenticationForm } from '@/components/auth/AuthenticationForm';
import { AuthorizationPage } from '@/components/auth/AuthorizationPage';
import { CheckLetterStatusBlock } from './letter/statusCheck/CheckLetterStatusBlock';
import { CookieConsentPage } from '@/components/CookieConsentPage';
import { DraftsBlock } from '@/components/home/drafts/DraftsBlock';
import { Grid, useMediaQuery } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { HelpPage } from '@/components/help/HelpPage';
import { HomePage } from '@/components/home/HomePage';
import { InboxBlock } from '@/components/home/inbox/InboxBlock';
import { InboxLetterPage } from '@/components/view/InboxLetterPage';
import { InitiativeLetterPage } from './letter/initiative/InitiativeLetterPage';
import { InitiativeLetterPayment } from './letter/initiative/InitiativeLetterPayment';
import { InitiativeLetterPaymentSuccess } from '@/components/letter/initiative/InitiativeLetterPaymentSuccess';
import { Main } from '@/components/Main';
import { MainAnswerBlock } from './create/main/answer/MainAnswerBlock';
import { MainCardPage } from './create/card/MainCardPage';
import { MainHeader } from './main/header/MainHeader';
import { MainLetterBlock } from './create/main/letter/MainLetterBlock';
import { MainPage } from '@/components/MainPage';
import { MetaType } from '@/types/MetaDTO';
import { MobileDraftsBlock } from '@/components/home/drafts/MobileDraftsBlock';
import { MobileHomePage } from '@/components/home/MobileHomePage';
import { MobileInboxBlock } from '@/components/home/inbox/MobileInboxBlock';
import { MobileOutboxBlock } from '@/components/home/outbox/MobileOutboxBlock';
import { MobileProfilePage } from '@/components/profile/MobileProfilePage';
import { NotFoundPage } from '@/components/NotFoundPage';
import { OfferPage } from '@/components/OfferPage';
import { OutboxBlock } from '@/components/home/outbox/OutboxBlock';
import { OutboxLetterPage } from './view/OutboxLetterPage';
import { Outlet, RouteObject, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { PhoneBindingGate } from '@/components/common/phone/PhoneBindingGate';
import { PhotoStatusBlock } from '@/components/letter/photoStatus/PhotoStatusBlock';
import { PrivateRoute } from './auth/PrivateRoute';
import { ProfilePage } from './profile/ProfilePage';
import {
  ROUTE_ABOUT,
  ROUTE_AUTHENTICATION,
  ROUTE_AUTHENTICATION_LOCKED,
  ROUTE_CHECK_LETTER_STATUS,
  ROUTE_COOKIE_CONSENT,
  ROUTE_CREATE,
  ROUTE_DRAFTS,
  ROUTE_ERROR,
  ROUTE_HELP,
  ROUTE_INBOX,
  ROUTE_INBOX_LETTER,
  ROUTE_LETTER_PHOTO_STATUS,
  ROUTE_MAIN,
  ROUTE_MAIN_CARD,
  ROUTE_MAIN_CREATE_ANSWER,
  ROUTE_MAIN_CREATE_LETTER,
  ROUTE_OFFER,
  ROUTE_OUTBOX,
  ROUTE_OUTBOX_LETTER,
  ROUTE_PAYMENT_INITIATIVE,
  ROUTE_PAYMENT_INITIATIVE_SUCCESS,
  ROUTE_PROFILE,
  ROUTE_REDIRECT,
  ROUTE_REGISTRATION,
  ROUTE_REGISTRATION_CONFIRM_REQUEST,
  ROUTE_REGISTRATION_CONFIRM_SUCCESS,
  ROUTE_REGISTRATION_SUCCESS,
  ROUTE_RESTORE,
  ROUTE_RESTORE_NEW,
  ROUTE_RESTORE_SUCCESS,
  ROUTE_VIEW_INITIATIVE,
} from '@/app/routes';
import { RegistrationConfirmRequest } from '@/components/auth/RegistrationConfirmRequest';
import { RegistrationConfirmSuccess } from '@/components/auth/RegistrationConfirmSuccess';
import { RegistrationForm } from '@/components/auth/RegistrationForm';
import { RegistrationSuccessForm } from '@/components/auth/RegistrationSuccessForm';
import { RestoreNewPasswordForm } from '@/components/auth/RestoreNewPasswordForm';
import { RestorePasswordForm } from '@/components/auth/RestorePasswordForm';
import { RestoreSuccessForm } from '@/components/auth/RestoreSuccessForm';
import { Theme } from '@mui/material/styles';
import { UserDTO } from '@/types/user/UserDTO';
import { UserLockedMessage } from '@/components/auth/UserLockedMessage';
import { config } from '@/app/config';
import { getCurrentUser } from '@/services/authSlice';
import {
  renderDepartmentChangeBlock,
  renderErrorBlock,
  renderHelpBlock,
  renderMainAnswerBlock,
  renderMainCardBlock,
  renderMainLetterBlock,
  renderProfileLetterBlock,
} from '@/components/routeBlockRenders';
import { useAppSelector } from '@/app/hooks';
import { useGetMetaDataQuery } from '@/services/api/metaApiSlice';
import React, { FC, Fragment, ReactElement, useMemo } from 'react';

/**
 * Puts every route group under one parent that carries the phone-binding barrier.
 *
 * @param groups The top-level route groups to place under the barrier.
 */
const withPhoneBindingBarrier = (groups: RouteObject[]): RouteObject[] => [
  {
    element: (
      <Fragment>
        <PhoneBindingGate handles={'REQUIRED'} />
        <Outlet />
      </Fragment>
    ),
    children: groups,
  },
];

export const Root: FC = (): ReactElement => {
  const { data: meta } = useGetMetaDataQuery(MetaType.DEFAULT);

  const authUser: UserDTO | undefined = useAppSelector(getCurrentUser);

  const isPrerender: boolean = navigator.userAgent === 'ReactSnap' || navigator.userAgent === 'Prerender';
  const isMobile: boolean = useMediaQuery((theme: Theme): string => theme.breakpoints.down('sm')) && !isPrerender;
  const isPad: boolean = useMediaQuery((theme: Theme): string => theme.breakpoints.down('md'));

  const router = useMemo(
    () =>
      createBrowserRouter(
        withPhoneBindingBarrier([
          {
            element: <Main />,
            children: [
              {
                element: <PrivateRoute>{isMobile ? <MobileHomePage /> : <HomePage />}</PrivateRoute>,
                children: [
                  {
                    path: ROUTE_INBOX,
                    element: isMobile ? <MobileInboxBlock /> : <InboxBlock />,
                  },
                  {
                    path: ROUTE_OUTBOX,
                    element: isMobile ? <MobileOutboxBlock /> : <OutboxBlock />,
                  },
                  {
                    path: ROUTE_DRAFTS,
                    element: isMobile ? <MobileDraftsBlock /> : <DraftsBlock />,
                  },
                ],
              },
              {
                path: ROUTE_CREATE,
                element: (
                  <PrivateRoute>
                    <Outlet />
                  </PrivateRoute>
                ),
                children: renderProfileLetterBlock(isMobile),
              },
              {
                path: ROUTE_PROFILE,
                element: <PrivateRoute>{isMobile ? <MobileProfilePage /> : <ProfilePage />}</PrivateRoute>,
              },
              {
                path: ROUTE_OUTBOX_LETTER,
                element: (
                  <PrivateRoute>
                    <OutboxLetterPage />
                  </PrivateRoute>
                ),
              },
              {
                path: ROUTE_INBOX_LETTER,
                element: (
                  <PrivateRoute>
                    <InboxLetterPage />
                  </PrivateRoute>
                ),
              },
            ],
          },
          {
            element: <AuthorizationPage />,
            children: [
              { path: ROUTE_AUTHENTICATION, element: <AuthenticationForm /> },
              { path: ROUTE_REGISTRATION, element: <RegistrationForm /> },
              { path: ROUTE_REGISTRATION_SUCCESS, element: <RegistrationSuccessForm /> },
              { path: ROUTE_RESTORE, element: <RestorePasswordForm /> },
              { path: ROUTE_RESTORE_SUCCESS, element: <RestoreSuccessForm /> },
              { path: ROUTE_RESTORE_NEW, element: <RestoreNewPasswordForm /> },
              { path: ROUTE_REGISTRATION_CONFIRM_REQUEST, element: <RegistrationConfirmRequest /> },
              { path: ROUTE_AUTHENTICATION_LOCKED, element: <UserLockedMessage /> },
              { path: ROUTE_REGISTRATION_CONFIRM_SUCCESS, element: <RegistrationConfirmSuccess /> },
            ],
          },
          {
            element: <MainPage />,
            children: [
              {
                path: ROUTE_MAIN,
                element: (
                  <Grid container={true} direction={'column'} spacing={5}>
                    <Grid item={true}>
                      <MainHeader />
                    </Grid>
                    <Grid item={true}>
                      <AboutPage isMainPage={true} />
                    </Grid>
                  </Grid>
                ),
              },
              {
                path: ROUTE_REDIRECT,
                element: <Outlet />,
                children: renderDepartmentChangeBlock(isMobile),
              },
              { path: ROUTE_CHECK_LETTER_STATUS, element: <CheckLetterStatusBlock isMobile={isMobile} /> },
              { path: ROUTE_LETTER_PHOTO_STATUS, element: <PhotoStatusBlock isMobile={isMobile} /> },
              {
                path: ROUTE_MAIN_CREATE_LETTER,
                element: <MainLetterBlock />,
                children: renderMainLetterBlock(isMobile, authUser),
              },
              {
                path: ROUTE_MAIN_CARD,
                element: <MainCardPage />,
                children: renderMainCardBlock(isMobile),
              },
              {
                path: ROUTE_MAIN_CREATE_ANSWER,
                element: <MainAnswerBlock />,
                children: renderMainAnswerBlock(isMobile, authUser),
              },
              {
                path: ROUTE_HELP,
                element: <HelpPage />,
                children: renderHelpBlock(isMobile, isPad),
              },
              { path: ROUTE_ABOUT, element: <AboutPage /> },
              { path: ROUTE_OFFER, element: <OfferPage /> },
              { path: ROUTE_COOKIE_CONSENT, element: <CookieConsentPage /> },
              { path: ROUTE_VIEW_INITIATIVE, element: <InitiativeLetterPage /> },
              { path: ROUTE_PAYMENT_INITIATIVE_SUCCESS, element: <InitiativeLetterPaymentSuccess /> },
              { path: ROUTE_PAYMENT_INITIATIVE, element: <InitiativeLetterPayment /> },
              {
                path: ROUTE_ERROR,
                element: <Outlet />,
                children: renderErrorBlock(),
              },
            ],
          },
          {
            path: '*',
            element: <NotFoundPage />,
          },
        ]),
        { basename: config.basePath },
      ),
    [isMobile, isPad, authUser],
  );

  return (
    <>
      {meta && (
        <Helmet>
          <title>{meta.title}</title>
          <meta name={'description'} content={meta.description} />
          <meta name={'keywords'} content={meta.keywords} />
        </Helmet>
      )}
      <RouterProvider router={router} />
    </>
  );
};
